import { Injectable } from "@angular/core";
import {
  AngularFirestore,
  AngularFirestoreCollection,
} from "@angular/fire/firestore";
import { User } from "../@core/data/users";
import { UserService } from "../@core/services/user.service";

import Announcement from "../models/announcement.model";
import * as firebase from "firebase/app";
import "firebase/firestore";
import { environment } from "../../environments/environment";
import { HttpClient } from "@angular/common/http";

@Injectable({
  providedIn: "root",
})
export class AnnouncementService {
  visibility = environment.visibility;
  baseUrl = environment.apiUrl;
  userLogin: User;
  private dbPath = "/announcements";
  contentRef: AngularFirestoreCollection<Announcement>;

  constructor(
    private http: HttpClient,
    private db: AngularFirestore,
    private userStore: UserService
  ) {
    this.contentRef = db.collection(this.dbPath);

    this.userStore.getCurrentUser().subscribe((user: User) => {
      this.userLogin = user;
    });
  }

  getHomeAnnouncements(): AngularFirestoreCollection<Announcement> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("category", "==", "system")
        .where("status", "==", 1)
        .orderBy("updatedOn", "desc")
        .limit(1)
    );
  }

  getGuides(): AngularFirestoreCollection<Announcement> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("category", "==", "guide")
        .where("status", "==", 1)
        .orderBy("updatedOn", "desc")
    );
  }

  getCrudGuides(): AngularFirestoreCollection<Announcement> {
    return this.db.collection(this.dbPath, (ref) =>
      ref
        .where("isDeleted", "==", false)
        .where("isActive", "==", true)
        .where("category", "==", "guide")
        .orderBy("updatedOn", "desc")
    );
  }

  getAnnouncements(limit?: number, cursor?: any): any {
    var db = firebase.default.firestore();
    var ref = db
      .collection(this.dbPath)
      .where("isDeleted", "==", false)
      .where("isActive", "==", true)
      .where("category", "==", "system")
      .orderBy("updatedOn", "desc");

    ref = limit ? ref.limit(limit) : ref.limit(10);

    if (cursor) {
      ref = ref.startAfter(cursor);
    }

    return ref.get();
  }

  get(id: string) {
    return this.contentRef.doc(id).valueChanges();
  }

  getById(id: string) {
    var db = firebase.default.firestore();
    var ref = db.collection(this.dbPath).doc(id);

    return ref.get();
  }

  create(content: Announcement): any {
    content.category = content.category ? content.category : "system";
    content.status = content.status ? content.status : 0;

    content.priority = content.priority ? content.priority : "info";
    content.startDate = content.startDate ? content.startDate : new Date();
    content.endDate = content.endDate ? content.endDate : new Date();
    content.responseType = content.responseType ? content.responseType : "";
    content.responseAction = content.responseAction
      ? content.responseAction
      : "";

    content.tags = content.tags ? content.tags : [];
    content.privacy = content.privacy ? content.privacy : false;

    content.imagesUrl = content.imagesUrl ? content.imagesUrl : [];
    content.videoUrl = content.videoUrl ? content.videoUrl : "";
    content.content = content.content ? content.content : "";
    content.noLiked = 0;
    content.noComment = 0;

    //Moderator
    content.isReviewed = false;
    content.isApproved = this.userLogin.admin ? true : false;
    content.approvedOn = new Date();
    content.approvedBy = this.userLogin.uid;
    content.approvedAuthor = this.userLogin;

    content.isActive = true;
    content.isDeleted = false;
    content.createdOn = new Date();
    content.createdBy = this.userLogin.uid;
    content.updatedOn = new Date();
    content.updatedBy = this.userLogin.uid;
    content.author = this.userLogin;
    content.timestamp = new Date().getTime();
    content.visibility = this.visibility;

    return this.contentRef.add({ ...content }).then((docRef) => {
      content.id = docRef.id;
      const syncData = { id: content.id, ...content };
      this.sync(syncData);

      return content;
    });
  }

  updateInfo(content: Announcement): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    return this.contentRef
      .doc(content.id)
      .update({
        title: content.title,
        description: content.description,
        coverUrl: content.coverUrl ? content.coverUrl : "",
        priority: content.priority ? content.priority : "info",
        startDate: content.startDate ? content.startDate : new Date(),
        endDate: content.endDate ? content.endDate : new Date(),
        responseType: content.responseType ? content.responseType : "",
        responseAction: content.responseAction ? content.responseAction : "",
        sourceType: content.sourceType,
        // content: content.content ? content.content : "",
        videoUrl: content.videoUrl ? content.videoUrl : "",
        imagesUrl: content.imagesUrl ? content.imagesUrl : [],
        tags: content.tags ? content.tags : [],
        privacy: content.privacy ? content.privacy : false,
        newComment: content.newComment,
        showComments: content.showComments,
        status: content.status ? content.status : 0,
        updatedOn: updatedOn,
        updatedBy: updatedBy,
        timestamp: new Date().getTime(),
        isReviewed: false,
      })
      .then((docRef) => {
        const syncData = { id: content.id, ...content };
        this.sync(syncData);

        return docRef;
      });
  }

  update(content: Announcement): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    return this.contentRef
      .doc(content.id)
      .update({
        title: content.title,
        description: content.description,
        coverUrl: content.coverUrl ? content.coverUrl : "",
        priority: content.priority ? content.priority : "info",
        startDate: content.startDate ? content.startDate : new Date(),
        endDate: content.endDate ? content.endDate : new Date(),
        responseType: content.responseType ? content.responseType : "",
        responseAction: content.responseAction ? content.responseAction : "",
        sourceType: content.sourceType,
        content: content.content ? content.content : "",
        videoUrl: content.videoUrl ? content.videoUrl : "",
        imagesUrl: content.imagesUrl ? content.imagesUrl : [],
        tags: content.tags ? content.tags : [],
        privacy: content.privacy ? content.privacy : false,
        newComment: content.newComment,
        showComments: content.showComments,
        status: content.status ? content.status : 0,
        updatedOn: updatedOn,
        updatedBy: updatedBy,
        timestamp: new Date().getTime(),
        isReviewed: false,
      })
      .then((docRef) => {
        const syncData = { id: content.id, ...content };
        this.sync(syncData);

        return docRef;
      });
  }

  confirm(content: Announcement): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;

    return this.contentRef.doc(content.id).update({
      status: 2,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  delete(id: string): Promise<void> {
    const updatedOn = new Date();
    const updatedBy = this.userLogin.uid;
    return this.contentRef.doc(id).update({
      isDeleted: true,
      updatedOn: updatedOn,
      updatedBy: updatedBy,
    });
  }

  sync(content) {
    content.createdOn ? delete content.createdOn : null;
    content.updatedOn ? delete content.updatedOn : null;
    content.content ? delete content.content : null;

    content.startDate = content.startDate ? content.startDate : null;
    content.endDate = content.endDate ? content.endDate : null;
    content.tags = JSON.stringify(content.tags);
    content.imagesUrl = JSON.stringify(content.imagesUrl);
    content.fsId = content.id ? content.id : "";

    if (environment.syncLive) {
      this.http.post(this.baseUrl + "/Announcements", content).subscribe({
        next: () => {},
        error: (err) => {},
      });
    }
  }
}
